/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../Components/ui/card";
import { Eye, EyeClosed, KeyRound, Mail, Building2 } from "lucide-react";
import { loginWithTenant, completeLogin, TenantOption } from "../utils/tenantApi";

function Login() {
    const [searchParams] = useSearchParams();
    const prefilledEmail = searchParams.get('email') || "";
    const welcomeMessage = searchParams.get('message') || "";
    
    // state variables for email and passwords
    const [email, setEmail] = useState<string>(prefilledEmail);
    const [password, setPassword] = useState<string>("");
    const [rememberme, setRememberme] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    
    // state variables for tenant selection
    const [showTenantSelection, setShowTenantSelection] = useState<boolean>(false);
    const [availableTenants, setAvailableTenants] = useState<TenantOption[]>([]);
    const [selectedTenant, setSelectedTenant] = useState<string>("");

    // state variable for error messages
    const [error, setError] = useState<string>(welcomeMessage);
    const errorMsgStyle = "mt-2 bg-red-500 p-3 text-white text-center font-bold rounded";
    const successMsgStyle = "mt-2 bg-green-500 p-3 text-white text-center font-bold rounded";
    const infoMsgStyle = "mt-2 bg-blue-500 p-3 text-white text-center font-bold rounded";
    const [messageStyle, setMessageStyle] = useState(welcomeMessage ? infoMsgStyle : errorMsgStyle);
    const navigate = useNavigate();
    
    useEffect(() => {
        if (welcomeMessage) {
            setMessageStyle(infoMsgStyle);
        }
    }, [welcomeMessage]);

    // handle change events for input fields
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === "email") setEmail(value);
        if (name === "password") setPassword(value);
        if (name === "rememberme") setRememberme(e.target.checked);
    };

    const handleRegisterClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        navigate("/register");
    }

    const handleShowPasswordClick = () => {
        
        setShowPassword(!showPassword); // toggle show password
    }

    // handle submit event for the form
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        // validate email and passwords
        if (!email || !password) {
            setError("Please fill in all fields");
            setMessageStyle(errorMsgStyle);
            return;
        }

        // clear error message
        setError("");
        setIsLoading(true);

        try {
            // Use the tenant-aware login function
            const response = await loginWithTenant(email, password, rememberme);
            
            console.log('Login successful:', response);
            
            // Check if this is an admin login
            if ('isAdmin' in response && response.isAdmin === true) {
                // Redirect to tenant selection page for admins
                navigate('/admin/select-tenant', { 
                    state: response 
                });
                return;
            }
            
            // Check if user needs to select a tenant
            if ('requiresTenantSelection' in response && response.requiresTenantSelection === true) {
                setAvailableTenants(response.availableTenants);
                setShowTenantSelection(true);
                setIsLoading(false);
                return;
            }
            
            setError("Successful Login.");
            setMessageStyle(successMsgStyle);
            
            // Redirect to home page for regular users
            setTimeout(() => {
                window.location.href = '/';
            }, 500);
        } catch (error: any) {
            console.error('Login error:', error);
            setError(error.message || "Error Logging In");
            setMessageStyle(errorMsgStyle);
        } finally {
            setIsLoading(false);
        }
    };

    // handle tenant selection
    const handleTenantSelection = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        if (!selectedTenant) {
            setError("Please select a tenant");
            setMessageStyle(errorMsgStyle);
            return;
        }

        setError("");
        setIsLoading(true);

        try {
            await completeLogin(selectedTenant);
            
            setError("Login Successful.");
            setMessageStyle(successMsgStyle);
            
            // Redirect to home page
            setTimeout(() => {
                window.location.href = '/';
            }, 500);
        } catch (error: any) {
            console.error('Tenant selection error:', error);
            setError(error.message || "Error completing login");
            setMessageStyle(errorMsgStyle);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-row min-h-screen justify-center items-center bg-gradient-to-br from-background to-secondary/20">
        <Card className="flex flex-col justify-self-center p-8">
            {showTenantSelection ? (
                <>
                    <h3 className="mb-6 text-3xl font-bold text-center">Select Tenant</h3>
                    <p className="mb-4 text-center text-gray-600 dark:text-gray-400">
                        You have access to multiple organizations. <br />Please select which one you'd like to access.
                    </p>
                    <form className="w-full max-w-sm" onSubmit={handleTenantSelection}>
                        <div className="mb-6 space-y-2">
                            {availableTenants.map((tenant) => (
                                <label 
                                    key={tenant.tenantId}
                                    className={`flex items-center p-4 border-2 rounded cursor-pointer transition-all ${
                                        selectedTenant === tenant.tenantId 
                                            ? 'border-accent bg-table-header' 
                                            : 'border-gray-300 hover:border-accent/50 hover:bg-table-header/50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="tenant"
                                        value={tenant.tenantId}
                                        checked={selectedTenant === tenant.tenantId}
                                        onChange={(e) => setSelectedTenant(e.target.value)}
                                        className="mr-3 accent-accent"
                                    />
                                    <Building2 className="mr-3 text-gray-500" />
                                    <div className="flex-1">
                                        <div className="font-semibold">{tenant.tenantName}</div>
                                        <div className="text-xs text-gray-500">
                                            {tenant.isActive ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <div className="md:flex md:items-center">
                            <button 
                                className="mb-4 w-full button-default" 
                                type="submit"
                                disabled={!selectedTenant}
                            >
                                Continue
                            </button>
                        </div>
                        <div className="md:flex md:items-center">
                            <button 
                                className="mb-4 w-full shadow bg-gray-600 hover:bg-gray-400 focus:shadow-outline focus:outline-none text-white font-bold py-2 px-4 rounded" 
                                type="button"
                                onClick={() => {
                                    setShowTenantSelection(false);
                                    setSelectedTenant("");
                                    setAvailableTenants([]);
                                }}
                            >
                                Back to Login
                            </button>
                        </div>
                        {error && <p className={messageStyle}>{error}</p>}
                        {isLoading && <div className="flex flex-row"><div className="flex flex-col m-auto"><span className="loader"></span></div></div>}
                    </form>
                </>
            ) : (
                <>
                    <h3 className="mb-6 text-3xl font-bold text-center">Login</h3>
                    <form className="w-full max-w-sm" onSubmit={handleSubmit}>
                <div className="md:flex md:items-center mb-6">
                     <div className="flex flex-row w-full bg-gray-200 rounded">
                    <label className="text-gray-500 font-bold content-center ml-1" htmlFor="email"><Mail /></label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="john.doe@example.com"
                        className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                        value={email}
                        onChange={handleChange}
                    />
                    </div>
                </div>
                <div className="md:flex md:items-center mb-6">
                    <div className="flex flex-row w-full bg-gray-200 rounded">
                    <label className="text-gray-500 font-bold content-center ml-1" htmlFor="password"><KeyRound /></label>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                placeholder="******"
                                className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"    
                                value={password}
                                onChange={handleChange}
                            />
                            <div className="content-center bg-gray-200 text-gray-500 p-2 rounded" onClick={handleShowPasswordClick}>{showPassword ? <Eye /> : <EyeClosed />}</div>
                    </div>
                </div>
                <div className="md:flex md:items-center mb-6">
                    <label className="text-gray-500 font-bold">
                        <input
                            className="mr-2 leading-tight"
                            type="checkbox"
                            id="rememberme"
                            name="rememberme"
                            checked={rememberme}
                            onChange={handleChange} />
                        <span className="text-sm">Remember Me</span>
                     </label>
                </div>
                <div className="md:flex md:items-center mb-3">
                    <button 
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm text-blue-600 hover:text-blue-400 underline"
                    >
                        Forgot Password?
                    </button>
                </div>
                <div className="md:flex md:items-center">
                    <button className="button-default w-full mb-6" type="submit">Login</button>
                </div>
                <div className="md:flex md:items-center">
                    <button className="button-default w-full" onClick={handleRegisterClick}>Register</button>
                </div>
                </form>
                {error && <p className={messageStyle}>{error}</p>}
                {isLoading && <div className="flex flex-row" ><div className="flex flex-col m-auto"><span className="loader"></span></div></div>}
                
                {/* <div className="mt-4 pt-4 border-t border-gray-700 text-center">
                    <button 
                        type="button"
                        onClick={() => navigate('/admin/tenants')}
                        className="text-xs text-gray-500 hover:text-gray-300"
                    >
                        PPMeasy Team Admin
                    </button>
                </div> */}
                </>
            )}
            </Card>
        </div>
    );
}

export default Login;
